import { rcrmFieldEntityFor } from '../field-identities.js';
import { CUSTOM_FIELD_ENTITIES, fieldDefinitionSchema, } from '../fields.js';
import { slugifyStage } from '../pipelines.js';
import { RCRM_ENTITY_RECORD_TYPE } from './common.js';
import { RCRM_OBSERVED_SELECT_OPTIONS } from './custom-field-options.js';
import { isRcrmRelayBookkeeping, RCRM_CUSTOM_FIELDS } from './maps.js';
/**
 * RecruitCRM's custom fields as CRM field definitions (TS-1753). This file is DATA: every custom
 * field RCRM defines, per entity, with the CRM key and label it is imported under. `seedRcrmCustomFields`
 * (`@crm/db`) creates the definitions and links each one to its RCRM field id in
 * `field_identities` (TS-1840), which is how the sync and the history import find the field again.
 *
 * Source: the Data Team's inventory (Victor Luz, 2026-10-29, Slack file
 * `rcrm_custom_fields_inventory.xlsx`; also `docs/recruitcrm/custom-fields-inventory.md`): 232
 * rows, field id, name and RCRM `field_type` as the live workspace has them. Names are copied
 * exactly: they are the snapshot a link stores and the sync compares at send time.
 *
 * - **Keys are permanent** (a definition's key never changes, values and exports use it), so they
 *   are written here rather than derived at run time: snake_case of RCRM's name, shortened by
 *   hand where RCRM's name is a whole question. Such a field keeps RCRM's question as its
 *   description.
 * - **Labels** are RCRM's name without a trailing colon, unless given.
 * - **Positions** are RCRM's field ids, so Settings → Fields lists them in RCRM's order under
 *   the "RecruitCRM" group ("RecruitCRM contact" for contact fields moved to another record).
 * - **Where a field lands** is its RCRM entity's CRM counterpart (`RCRM_FIELD_IMPORT_ENTITY`),
 *   except the contact fields that describe the client account (`CONTACT_ACCOUNT_FIELDS`).
 *   Only a field that lands on its RCRM entity's counterpart can be linked (a link pairs a CRM
 *   entity with ITS RCRM entity); the others are created unlinked, and this file is what maps
 *   them back to their RCRM id.
 */
/** RCRM `field_type` → the CRM field type it is imported as; null: not importable. */
export const RCRM_TO_CRM_FIELD_TYPE = {
    text: 'text',
    longtext: 'long_text',
    number: 'number',
    date: 'date',
    date_time: 'datetime',
    dropdown: 'single_select',
    multiselect: 'multi_select',
    checkbox: 'boolean',
    user: 'user',
    email: 'email',
    phonenumber: 'phone',
    // An attachment URL: no CRM type holds a file (documents are their own model).
    file: null,
};
/**
 * The CRM entity an RCRM entity's custom fields are imported into. Explicit rather than the
 * inverse of `CRM_TO_RCRM_FIELD_ENTITY` (the push map): deals are imported but never pushed.
 * An RCRM deal is the CRM's deal, a sub-entity of the project (decided 2026-10-30), not the
 * project itself, although TS-1859 seeds RCRM's deal STAGES into the project pipeline.
 */
export const RCRM_FIELD_IMPORT_ENTITY = {
    candidates: 'candidates',
    companies: 'companies',
    contacts: 'contacts',
    jobs: 'jobs',
    deals: 'deals',
};
/**
 * RCRM contact fields that describe the client ACCOUNT rather than the person (decided
 * 2026-10-30): RCRM keeps them on the contact because its company is often "{Person} (no co.)".
 * Account attributes go on the company; the purchase, its payments and its placements on the
 * project. The other contact fields stay on the contact.
 */
const CONTACT_FIELDS_ON_COMPANY = [
    7, 31, 32, 40, 41, 42, 43, 44, 45, 46, 47, 48, 49, 53, 54, 55, 56, 57, 58, 59, 60, 61, 62, 63, 71,
    72, 76, 77, 78,
];
const CONTACT_FIELDS_ON_PROJECT = [
    2, 4, 30, 50, 51, 52, 64, 65, 66, 67, 68, 69, 70, 73, 74, 75, 79, 80,
];
const CONTACT_ACCOUNT_FIELDS = Object.fromEntries([
    ...CONTACT_FIELDS_ON_COMPANY.map((id) => [id, 'companies']),
    ...CONTACT_FIELDS_ON_PROJECT.map((id) => [id, 'projects']),
]);
/**
 * Contact fields a STANDARD column of their CRM record already holds: the history import writes
 * them there, and a custom field beside the column would be a second "Website".
 */
const CONTACT_STANDARD_COLUMNS = {
    41: 'companies.website',
    46: 'companies.industry',
};
/** The heading imported fields sit under in Settings → Fields and on record pages. */
export const RCRM_FIELD_GROUP = 'RecruitCRM';
function rcrmFieldLabel(name) {
    return name.trim().replace(/\s*:$/, '');
}
function fields(entity, rows, moved = {}, columns = {}) {
    return rows.map(([fieldId, name, type, key, label]) => ({
        entity,
        fieldId,
        name,
        type,
        crmEntity: moved[fieldId] ?? RCRM_FIELD_IMPORT_ENTITY[entity],
        key,
        label: label ?? rcrmFieldLabel(name),
        ...(label === undefined ? {} : { description: name }),
        ...(columns[fieldId] === undefined ? {} : { column: columns[fieldId] }),
    }));
}
const TYPING_SCORE = 'Typing Speed Test Score Number: Go to Remoteleverage.com/typing to test. MUST BE ON COMPUTER/LAPTOP';
const TYPING_SCREENSHOT = 'Typing Speed Test Results Screenshot (Take a screenshot of results on a computer)';
/** Every RCRM custom field, per RCRM entity, in RCRM's id order. */
export const RCRM_CUSTOM_FIELD_INVENTORY = {
    candidates: fields('candidates', [
        [
            1,
            'English Voice Recording Audio Link (You can use Vocaroo/Speakpipe and put the link here). 30 Seconds to 2 mins ideally just introducing yourself, work background, experience, etc. Make sure audio quality is good.',
            'text',
            'english_voice_recording',
            'English voice recording',
        ],
        [2, 'Candidate Stage', 'dropdown', 'candidate_stage'],
        [3, 'Expected hourly rate in US Dollars?', 'text', 'expected_hourly_rate'],
        [4, 'How did you find us?', 'text', 'how_did_you_find_us'],
        [
            5,
            'What Do You Think is the BEST POSITION for you given your experience, if you can only pick ONE?',
            'multiselect',
            'best_position',
            'Best position',
        ],
        [6, 'Positions Approved for:', 'multiselect', 'positions_approved_for'],
        [7, 'Assessment Score', 'number', 'assessment_score'],
        [8, 'Candidate Source', 'text', 'candidate_source'],
        [9, 'Interview Date', 'date', 'interview_date'],
        [10, 'Languages spoken:', 'text', 'languages_spoken'],
        [11, 'Portfolio Link (if applicable)', 'text', 'portfolio_link'],
        [12, 'Are you open to Full Time, Part Time, Or Both?', 'dropdown', 'full_or_part_time'],
        [13, TYPING_SCORE, 'text', 'typing_speed_score', 'Typing speed test score'],
        [14, TYPING_SCREENSHOT, 'file', 'typing_speed_screenshot', 'Typing speed test screenshot'],
        [15, 'Resume (Manual upload)', 'text', 'resume_manual_upload'],
        [16, 'Missed Interview Warning Email Sent', 'dropdown', 'missed_interview_warning_email_sent'],
        [17, 'Application Date', 'date', 'application_date'],
        [18, 'Hired Date:', 'date', 'hired_date'],
        [19, 'Phase 3 Automation Sent', 'date', 'phase_3_automation_sent'],
        [20, 'Missed Interview Automation Sent', 'date', 'missed_interview_automation_sent'],
        [
            21,
            'Missed Interview 2nd Attempt (Final) Automation Sent',
            'date',
            'missed_interview_final_automation_sent',
        ],
        [22, 'Failed Interview Automation Sent', 'date', 'failed_interview_automation_sent'],
        [23, 'Phase 1 Automations Sent', 'date', 'phase_1_automations_sent'],
        [24, 'Gender:', 'dropdown', 'gender'],
        [25, 'Github URL:', 'text', 'github_url'],
        [26, 'Personal Website:', 'text', 'personal_website'],
        [
            27,
            'Have you built any product that we could look at? Share the URLs in this field',
            'longtext',
            'built_product_urls',
            'Built product URLs',
        ],
        [28, 'Date of Birth', 'date', 'date_of_birth'],
        [29, 'Clean First Name', 'text', 'clean_first_name'],
        [30, 'Internal Recruiter Assigned', 'text', 'internal_recruiter_assigned'],
        [31, 'Internal Candidate Stage', 'dropdown', 'internal_candidate_stage'],
        [32, 'Interview', 'date_time', 'interview'],
        [33, 'Internet Speed', 'text', 'internet_speed'],
        [34, 'Role', 'text', 'role'],
        [35, 'Pay Rate', 'text', 'pay_rate'],
        [36, 'T1 Recruiter', 'user', 't1_recruiter'],
        [37, 'T2 Recruiter', 'user', 't2_recruiter'],
        [38, 'Voice Note Approved At', 'date_time', 'voice_note_approved_at'],
        [39, 'Authorized to travel', 'checkbox', 'authorized_to_travel'],
        [40, 'Completed Application Date', 'date', 'completed_application_date'],
        [41, 'Interview RSVP', 'text', 'interview_rsvp'],
        [42, 'Parsed Resume', 'file', 'parsed_resume'],
        [43, 'Alternate Phone', 'text', 'alternate_phone'],
        [44, 'Parsed Portfolio', 'file', 'parsed_portfolio'],
        [45, 'Internal Recruiter Source', 'text', 'internal_recruiter_source'],
        [46, 'candidate_public_id', 'text', 'candidate_public_id'],
        [47, 'candidate_has_video_preview', 'text', 'candidate_has_video_preview'],
    ]),
    contacts: fields('contacts', [
        [1, 'Contact Source', 'text', 'contact_source'],
        [2, 'Latest Amount Paid?', 'number', 'latest_amount_paid'],
        [3, 'Agreement Signed', 'checkbox', 'agreement_signed'],
        [4, 'Payment Date', 'date', 'payment_date'],
        [5, "How Many VA's Placed?", 'number', 'vas_placed'],
        [6, 'Agreement Link', 'text', 'agreement_link'],
        [7, 'Total Amount Paid?', 'number', 'total_amount_paid'],
        [8, 'Company Revenue', 'text', 'company_revenue'],
        [9, 'Hiring Manager', 'text', 'hiring_manager'],
        [10, 'Affiliate Code', 'text', 'affiliate_code'],
        [11, 'Individual Hiring or Bundle?', 'text', 'individual_hiring_or_bundle'],
        [12, 'utm_source', 'text', 'utm_source'],
        [13, 'utm_medium', 'text', 'utm_medium'],
        [14, 'utm_campaign', 'text', 'utm_campaign'],
        [15, 'utm_content', 'text', 'utm_content'],
        [16, 'ip_address', 'text', 'ip_address'],
        [17, 'gclid', 'text', 'gclid'],
        [18, 'fbclid', 'text', 'fbclid'],
        [19, 'msclkid', 'text', 'msclkid'],
        [20, 'How was this contact created?', 'dropdown', 'how_was_this_contact_created'],
        [21, 'meeting_booked', 'checkbox', 'meeting_booked'],
        [22, 'meeting_date', 'date_time', 'meeting_date'],
        [23, 'Onboarding Scheduled', 'text', 'onboarding_scheduled'],
        [24, 'Onboarding Completed', 'text', 'onboarding_completed'],
        [25, 'Hire Type', 'dropdown', 'hire_type'],
        [26, 'Original Hiring Manager', 'text', 'original_hiring_manager'],
        [27, 'Account Manager', 'text', 'account_manager'],
        [28, 'Sales Rep Assigned', 'text', 'sales_rep_assigned'],
        [29, 'Rebooked By', 'user', 'rebooked_by'],
        [30, 'Product', 'multiselect', 'product'],
        [31, 'VA Counter', 'number', 'va_counter'],
        [32, 'COR Counter', 'number', 'cor_counter'],
        [33, 'Payment Emails', 'text', 'payment_emails'],
        [34, 'Relay Sync At', 'date_time', 'relay_sync_at'],
        [35, 'Address', 'text', 'address'],
        [36, 'utm_term', 'text', 'utm_term'],
        [37, 'Secondary Contact Email', 'email', 'secondary_contact_email'],
        [38, 'Secondary Contact Name', 'text', 'secondary_contact_name'],
        [39, 'Secondary Contact Phone', 'text', 'secondary_contact_phone'],
        [40, 'Parent', 'text', 'parent'],
        [41, 'Website', 'text', 'website'],
        [42, 'Zip Code', 'text', 'zip_code'],
        [43, '# of Open Jobs', 'text', 'open_jobs_count'],
        [44, 'Enriched Company Revenue', 'text', 'enriched_company_revenue'],
        [45, 'Current Company Revenue', 'text', 'current_company_revenue'],
        [46, 'Industry', 'text', 'industry'],
        [47, 'Sub Industry', 'text', 'sub_industry'],
        [48, 'Business Email', 'text', 'business_email'],
        [49, 'Company Size', 'text', 'company_size'],
        [50, 'COR Enrolled', 'multiselect', 'cor_enrolled'],
        [51, 'COR Enrollment Date', 'date', 'cor_enrollment_date'],
        [52, 'COR Service Termination Date', 'date', 'cor_service_termination_date'],
        [53, 'Communication Preference', 'dropdown', 'communication_preference'],
        [54, 'Active Complaint', 'dropdown', 'active_complaint'],
        [55, 'Chargeback Status', 'dropdown', 'chargeback_status'],
        [56, 'Chargeback Amount', 'text', 'chargeback_amount'],
        [57, 'Client VA Manager', 'text', 'client_va_manager'],
        [58, 'NPS General Client Score', 'text', 'nps_general_client_score'],
        [59, 'NPS on the Account Manager', 'text', 'nps_account_manager'],
        [60, 'Client Referred', 'text', 'client_referred'],
        [61, 'Client Closed', 'text', 'client_closed'],
        [62, 'Referral Value', 'text', 'referral_value'],
        [63, 'Referral Date', 'date', 'referral_date'],
        // The inventory lists id 64 twice: "VA Name(s)" (4,627 records, 275 with a value) and "VA
        // Name" (18, none) — a rename whose direction the counts do not show. The name with data is
        // taken; if RCRM's live name is the other one, the sync skips the value
        // (`rcrm-field-renamed`) until the link's snapshot is updated.
        [64, 'VA Name(s)', 'text', 'va_names'],
        [65, 'Placement Date', 'date', 'placement_date'],
        [66, 'Placement Type', 'dropdown', 'placement_type'],
        [67, 'Active COR VA', 'dropdown', 'active_cor_va'],
        [68, 'VA COR End Date', 'date', 'va_cor_end_date'],
        [69, 'VA Separation End Date', 'date', 'va_separation_end_date'],
        [70, 'VA Replacement Expiration Date', 'date', 'va_replacement_expiration_date'],
        [71, 'Total VAs', 'text', 'total_vas'],
        [72, 'Total Active COR VAs', 'text', 'total_active_cor_vas'],
        [73, 'COR Deposit', 'dropdown', 'cor_deposit'],
        [74, 'COR Deposit Value', 'text', 'cor_deposit_value'],
        [75, 'Unused Bundle Slots', 'number', 'unused_bundle_slots'],
        [76, 'Expansion Potential', 'text', 'expansion_potential'],
        [77, 'Potential Next Opportunity', 'text', 'potential_next_opportunity'],
        [78, 'Current Recurring Revenue', 'text', 'current_recurring_revenue'],
        [79, 'Replacements Used', 'number', 'replacements_used'],
        [80, 'Hires Purchased', 'number', 'hires_purchased'],
    ], CONTACT_ACCOUNT_FIELDS, CONTACT_STANDARD_COLUMNS),
    companies: fields('companies', [
        [1, 'Company Domain', 'text', 'company_domain'],
        [2, 'Company Logo', 'file', 'company_logo'],
        [3, 'Enriched Email', 'text', 'enriched_email'],
        [4, 'Enriched Name', 'text', 'enriched_name'],
        [5, 'Enriched Phone', 'phonenumber', 'enriched_phone'],
        [6, 'Enriched Website', 'text', 'enriched_website'],
        [7, 'Enriched City', 'text', 'enriched_city'],
        [8, 'Enriched State', 'text', 'enriched_state'],
        [9, 'Enriched Country', 'text', 'enriched_country'],
        [10, 'Enriched Zip Code', 'text', 'enriched_zip_code'],
        [11, 'Enriched LinkedIn Profile', 'text', 'enriched_linkedin_profile'],
        [12, 'Revenue', 'text', 'revenue'],
        [13, 'Enriched Revenue', 'text', 'enriched_revenue'],
        [14, 'Enriched Company Size', 'text', 'enriched_company_size'],
        [15, 'Enriched Summary', 'longtext', 'enriched_summary'],
        [16, 'Enriched NAICS', 'text', 'enriched_naics'],
        [17, 'Enriched Industry', 'text', 'enriched_industry'],
        [18, 'Enriched Sub Industry', 'text', 'enriched_sub_industry'],
        [19, 'Enriched Technologies', 'text', 'enriched_technologies'],
        [20, 'Enriched Categories', 'text', 'enriched_categories'],
        [21, 'Enriched Type', 'text', 'enriched_type'],
        [22, 'Enriched Year Founded', 'text', 'enriched_year_founded'],
        [23, 'Enriched Address', 'text', 'enriched_address'],
    ]),
    jobs: fields('jobs', [
        [
            1,
            'Looking to hire from the following Regions:',
            'dropdown',
            'hiring_regions',
            'Hiring regions',
        ],
        [2, 'Recruiter', 'text', 'recruiter'],
        [3, 'Sales Representative', 'text', 'sales_representative'],
        [4, 'Account Manager', 'text', 'account_manager'],
        [5, TYPING_SCORE, 'text', 'typing_speed_score', 'Typing speed test score'],
        [6, TYPING_SCREENSHOT, 'file', 'typing_speed_screenshot', 'Typing speed test screenshot'],
        [7, 'Individual Hiring or Bundle?', 'text', 'individual_hiring_or_bundle'],
        [8, 'Onboarding', 'date_time', 'onboarding'],
        [9, 'Interview', 'date_time', 'interview'],
        [10, 'Client Time Zone:', 'text', 'client_time_zone'],
        [11, 'Hiring Fee Percentage:', 'text', 'hiring_fee_percentage'],
        [12, 'Payment Method Saved on File:', 'dropdown', 'payment_method_saved_on_file'],
        [13, 'Payment Terms (Full, Split, Financing)', 'text', 'payment_terms'],
        [14, 'Replacement Guarantee End', 'date', 'replacement_guarantee_end'],
        [15, 'attended_onboarding_call', 'text', 'attended_onboarding_call'],
        [16, 'Hire Type', 'dropdown', 'hire_type'],
        [17, 'Location', 'dropdown', 'location'],
        [18, 'Commissions + Bonuses?', 'dropdown', 'commissions_bonuses'],
        [19, 'Job ID', 'text', 'job_id'],
        [20, 'Key Qualification (if any)', 'text', 'key_qualification'],
        [21, 'Tag', 'multiselect', 'tag'],
        [22, 'Job Post Title Long', 'text', 'job_post_title_long'],
        // RCRM spells it "Catagory"; the name stays RCRM's (the link snapshot), the CRM's is fixed.
        [23, 'Job Catagory', 'dropdown', 'job_category', 'Job Category'],
        [24, 'Job role', 'longtext', 'job_role'],
        [25, 'Last Contacted On', 'date_time', 'last_contacted_on'],
        [26, 'Experience (Months)', 'text', 'experience_months'],
        [27, 'Replacements', 'dropdown', 'replacements'],
        [28, 'Bundle Credit', 'text', 'bundle_credit'],
        [29, 'Top VA Presentation', 'date_time', 'top_va_presentation'],
        [30, 'Product', 'multiselect', 'product'],
        [31, 'Hiring Manager', 'text', 'hiring_manager'],
        [32, 'onboarding_date', 'date', 'onboarding_date'],
        [33, 'interview_booked', 'text', 'interview_booked'],
        [34, 'interview_date', 'date', 'interview_date'],
        [35, 'Difficulty', 'dropdown', 'difficulty'],
        [36, 'AI Analysis', 'longtext', 'ai_analysis'],
        [37, 'Recruiter Email', 'email', 'recruiter_email'],
        [38, 'Replacement Date', 'date_time', 'replacement_date'],
        [39, 'Replacement User', 'text', 'replacement_user'],
        [40, 'Replacement User Email', 'text', 'replacement_user_email'],
        [41, 'RevOps Recovery Stage', 'dropdown', 'revops_recovery_stage'],
        [42, 'RevOps Client Type', 'dropdown', 'revops_client_type'],
        [43, 'Coordinator', 'text', 'coordinator'],
        [44, 'Previous Hiring Manager', 'text', 'previous_hiring_manager'],
        [45, 'Onboarding Form Note', 'text', 'onboarding_form_note'],
        [46, 'Company Revenue', 'text', 'company_revenue'],
    ]),
    deals: fields('deals', [
        [1, 'Deal Source', 'text', 'deal_source'],
        [2, 'Meeting Status', 'dropdown', 'meeting_status'],
        [3, 'Qualified?', 'dropdown', 'qualified'],
        [4, 'Next Step?', 'dropdown', 'next_step'],
        [5, 'Consultation Date', 'date', 'consultation_date'],
        [6, 'Closing Attempts?', 'dropdown', 'closing_attempts'],
        [7, 'Company Revenue?', 'text', 'company_revenue'],
        [8, 'Business Industry?', 'text', 'business_industry'],
        [9, 'Last Referral/Upsell Attempt', 'date', 'last_referral_upsell_attempt'],
        [10, 'Phone', 'text', 'phone'],
        [11, 'Email', 'text', 'email'],
        [12, 'Hire Type', 'dropdown', 'hire_type'],
        [13, 'Total Paid', 'number', 'total_paid'],
        [14, 'utm_source', 'text', 'utm_source'],
        [15, 'utm_medium', 'text', 'utm_medium'],
        [16, 'utm_campaign', 'text', 'utm_campaign'],
        [17, 'utm_content', 'text', 'utm_content'],
        [18, 'ip_address', 'text', 'ip_address'],
        [19, 'fbclid', 'text', 'fbclid'],
        [20, 'gclid', 'text', 'gclid'],
        [21, 'msclkid', 'text', 'msclkid'],
        [22, 'Quickbooks Invoice ID', 'longtext', 'quickbooks_invoice_id'],
        [23, 'Account Executive', 'text', 'account_executive'],
        [24, 'Last Contacted On', 'date_time', 'last_contacted_on'],
        [25, 'Saved Date', 'date', 'saved_date'],
        [26, 'Onboarding Date', 'date_time', 'onboarding_date'],
        [27, 'Rebooked By', 'user', 'rebooked_by'],
        [28, 'Product', 'multiselect', 'product'],
        [29, 'Payment Emails', 'text', 'payment_emails'],
        [30, 'Symbo Prospect ID', 'text', 'symbo_prospect_id'],
        [31, 'Deal Information', 'longtext', 'deal_information'],
        [32, 'Pilot Note', 'longtext', 'pilot_note'],
        [33, 'utm_term', 'text', 'utm_term'],
        [34, 'Refund processed?', 'checkbox', 'refund_processed'],
        [35, 'Fulfillment Date', 'date', 'fulfillment_date'],
    ]),
};
/** The inventory as one list, entity by entity. */
export function rcrmInventoryFields() {
    return Object.values(RCRM_CUSTOM_FIELD_INVENTORY).flat();
}
/**
 * Why a field is not imported. The first five are decided from this file alone
 * (`planRcrmFieldImport`); `retired` and `key-taken` by the seed against the organisation's
 * definitions.
 *
 * - `column`: the CRM keeps the field as a column — one the sync writes through
 *   `RCRM_CUSTOM_FIELDS` (a link to its id is refused, TS-1840), or a standard column of the
 *   record it lands on (`CONTACT_STANDARD_COLUMNS`).
 * - `file`: an attachment, no CRM type for it.
 * - `entity-not-enabled`: the CRM entity cannot carry custom fields yet (`CUSTOM_FIELD_ENTITIES`;
 *   none today: contacts, jobs and deals joined with TS-1840). A later run imports them.
 * - `options-pending`: a dropdown or multiselect with no option list: none supplied, and no value
 *   observed in RCRM's data.
 * - `relay-bookkeeping`: Relay's own stamp on the RCRM record (contact 34 "Relay Sync At"), not data
 *   about it (`isRcrmRelayBookkeeping`).
 * - `retired`: imported once, then retired by an admin; it is not brought back.
 * - `key-taken`: a definition with the key exists with another type, or is linked to another
 *   RCRM field; nothing is changed, a person decides.
 */
export const RCRM_FIELD_IMPORT_SKIP_REASONS = [
    'column',
    'file',
    'entity-not-enabled',
    'options-pending',
    'relay-bookkeeping',
    'retired',
    'key-taken',
];
function isCustomFieldEntity(entity) {
    return CUSTOM_FIELD_ENTITIES.includes(entity);
}
/** RCRM option labels as CRM select options: the label as RCRM spells it, a slug as the value. */
export function rcrmSelectOptions(labels) {
    const out = [];
    const values = new Set();
    const seen = new Set();
    for (const raw of labels) {
        const label = raw.trim();
        if (!label || seen.has(label))
            continue;
        seen.add(label);
        let value = slugifyStage(label);
        for (let n = 2; values.has(value); n++)
            value = `${slugifyStage(label)}-${n}`;
        values.add(value);
        out.push({ value, label });
    }
    return out;
}
/**
 * What the import does with one RCRM field: the CRM definition to create, or why not.
 *
 * `options` are the select field's option labels; without them, the values the Data Team observed
 * in RCRM's data (`RCRM_OBSERVED_SELECT_OPTIONS`). A select field with neither is skipped as
 * `options-pending`, never created with invented options.
 * TODO(TS-1753): a crawler reading the options from the RecruitCRM API directly
 * (`GET /v1/custom-fields/{entity}`, as Relay reads `field_options ?? options ?? field_values`)
 * still has to supply the fields nobody ever filled in, and the options nobody ever picked.
 */
export function planRcrmFieldImport(field, options = RCRM_OBSERVED_SELECT_OPTIONS[`${field.entity}:${field.fieldId}`]) {
    const skip = (reason) => ({
        action: 'skip',
        field,
        reason,
    });
    if (isRcrmRelayBookkeeping(field.entity, field.fieldId))
        return skip('relay-bookkeeping');
    const type = RCRM_TO_CRM_FIELD_TYPE[field.type];
    if (type === null)
        return skip('file');
    if (field.column || rcrmColumnFieldIds(field.entity).has(field.fieldId))
        return skip('column');
    const { crmEntity } = field;
    if (!isCustomFieldEntity(crmEntity))
        return skip('entity-not-enabled');
    const selectOptions = type === 'single_select' || type === 'multi_select' ? rcrmSelectOptions(options ?? []) : null;
    if (selectOptions?.length === 0)
        return skip('options-pending');
    const ownEntity = crmEntity === RCRM_FIELD_IMPORT_ENTITY[field.entity];
    return {
        action: 'import',
        field,
        crmEntity,
        linkable: rcrmFieldEntityFor(crmEntity) === field.entity,
        definition: fieldDefinitionSchema.parse({
            entity: crmEntity,
            key: field.key,
            label: field.label,
            type,
            // A field moved off its RCRM record says where it came from: "RecruitCRM contact".
            group: ownEntity
                ? RCRM_FIELD_GROUP
                : `${RCRM_FIELD_GROUP} ${RCRM_ENTITY_RECORD_TYPE[field.entity]}`,
            position: field.fieldId,
            ...(field.description === undefined ? {} : { description: field.description }),
            ...(selectOptions ? { options: selectOptions } : {}),
        }),
    };
}
/** The RCRM field ids the sync writes from CRM columns (`RCRM_CUSTOM_FIELDS` fallback ids). */
export function rcrmColumnFieldIds(entity) {
    const refs = entity === 'deals' ? {} : RCRM_CUSTOM_FIELDS[entity];
    return new Set(Object.values(refs).map((r) => r.fallbackId));
}
//# sourceMappingURL=custom-fields.js.map